import { DeviceStatus } from '@/api/deviceApi'
import { AccessDeniedPage } from '@/app/pages/AccessDeniedPage'
import { deviceSortFromSearch } from '@/app/pages/DeviceManagement/deviceSortParam'
import { METERS_MODULE_IDS } from '@/app/pages/Organizations/Meters/metersConfig'
import {
	ApiKeysSettingsPage,
	AuditLogsPage,
	DeviceManagementPage,
	DeviceStatePage,
	MetadataPage,
	OrganizationBillingPage,
	OrganizationGeneralPage,
	OrganizationMetersLayout,
	OrganizationMetersList,
	RemoteCommandsPage,
	SettingsPage,
	StreamsPage,
	UsersSettingsPage,
} from '@/app/routes/lazyPages'
import {
	apiTypeSchema,
	orgIdParamsSchema,
	statusParamsSchema,
	tabAllOnlySchema,
	tabAnySchema,
} from '@/app/routes/params'
import { organizationProjectRouteTree } from '@/app/routes/projectRoutes'
import { redirectToAllTab } from '@/app/routes/redirectToAllTab'
import { rootRoute } from '@/app/routes/rootRoutes'
import { resolveQuotaTenantId } from '@/hooks/access-control/resolveQuotaTenantId'
import {
	authenticatedRouteGuard,
	consoleOnlyRouteGuard,
	organizationLevelRouteGuard,
	platformOnlyRouteGuard,
	prefetchQuota,
	redirectToFirstOrg,
} from '@/lib/routeGuards'
import { resolveOrgId } from '@/lib/routeScope'
import { accessDeniedSearchSchema, deviceSearchSchema, subscriptionPlansSearchSchema } from '@/lib/searchSchemas'
import { userBelongsToOrganization } from '@/stores/auth'
import { useAuthStore } from '@/stores/authStore'
import { useOrgDeviceStore } from '@/stores/orgDeviceStore'
import { useProjectsStore } from '@/stores/projectsStore'
import type { MetersModule } from '@/types/organization'
import { Outlet, createRoute, redirect } from '@tanstack/react-router'
import { z } from 'zod'

/**
 * Organization scoped routes, everything under `/orgs/$orgId` that names no project.
 *
 * Owner only, and each entry point resolves the segment to an identifier before any guard or store
 * sees it, so nothing downstream compares a URL segment against organization data.
 */
export const organizationRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: '/orgs/$orgId',
	parseParams: params => orgIdParamsSchema.parse(params),
	component: () => <Outlet />,
	beforeLoad: ({ context, params, location }) => {
		authenticatedRouteGuard(context, { location })
		const user = useAuthStore.getState().user
		if (!user) {
			throw redirectToFirstOrg()
		}

		const orgId = resolveOrgId(params.orgId)
		if (!userBelongsToOrganization(user, orgId)) {
			throw redirectToFirstOrg()
		}

		useAuthStore.getState().setCurrentOrganizationId(orgId)
	},
})

export const organizationBillingRoute = createRoute({
	getParentRoute: () => organizationRoute,
	path: '/billing',
	component: () => <Outlet />,
	beforeLoad: ({ context }) => {
		// Billing is only available on Console deployments
		consoleOnlyRouteGuard(context)
	},
})

export const organizationBillingIndexRoute = createRoute({
	getParentRoute: () => organizationBillingRoute,
	path: '/',
	beforeLoad: ({ params, search }) => {
		throw redirect({
			to: '/orgs/$orgId/billing/$type',
			params: { ...params, type: 'plan-details' },
			search,
			replace: true,
		})
	},
})

export const PaymentTypeSchema = z.object({
	type: z.enum(['plan-details', 'payment-history']),
})

export const organizationBillingTypeRoute = createRoute({
	getParentRoute: () => organizationBillingRoute,
	path: '/$type',
	parseParams: params => PaymentTypeSchema.parse(params),
	validateSearch: subscriptionPlansSearchSchema,
	component: () => <OrganizationBillingPage />,
})

export const organizationAccessDeniedRoute = createRoute({
	getParentRoute: () => organizationRoute,
	path: '/access-denied',
	validateSearch: accessDeniedSearchSchema,
	component: () => <AccessDeniedPage />,
})

export const organizationAuditLogsRoute = createRoute({
	getParentRoute: () => orgSettingsRoute,
	path: '/audit-logs',
	component: () => <Outlet />,
	beforeLoad: ({ context, params }) => {
		const orgId = resolveOrgId(params.orgId)
		organizationLevelRouteGuard(context, orgId)
		useAuthStore.getState().clearCurrentTenantUnlessBelongsToOrg(orgId)
	},
})

export const organizationAuditLogsIndexRoute = createRoute({
	getParentRoute: () => organizationAuditLogsRoute,
	path: '/',
	beforeLoad: redirectToAllTab('/orgs/$orgId/settings/audit-logs/$type'),
})

export const organizationAuditLogsTypeRoute = createRoute({
	getParentRoute: () => organizationAuditLogsRoute,
	path: '/$type',
	parseParams: params => tabAnySchema.parse(params),
	component: () => <AuditLogsPage />,
})

export const organizationInventoryRoute = createRoute({
	getParentRoute: () => organizationRoute,
	path: '/inventory',
	component: () => <Outlet />,
	beforeLoad: async ({ context, params, location }) => {
		const orgId = resolveOrgId(params.orgId)
		platformOnlyRouteGuard(context)
		organizationLevelRouteGuard(context, orgId)
		useAuthStore.getState().clearCurrentTenantUnlessBelongsToOrg(orgId)

		let rootTenantId = useProjectsStore.getState().rootTenantId
		if (!rootTenantId) {
			try {
				await useProjectsStore.getState().fetchProjects(undefined, undefined, undefined)
			} catch {
				// fetchProjects sets its own error state; proceed with whatever rootTenantId is available
			}
			rootTenantId = useProjectsStore.getState().rootTenantId
		}

		const { user, currentTenant } = useAuthStore.getState()
		const quotaTenantId = resolveQuotaTenantId({
			currentTenant,
			pathname: location.pathname,
			rootTenantId,
			organizationId: orgId,
			user,
		})
		await prefetchQuota(quotaTenantId ?? undefined)
	},
})

// Index route for inventory that redirects to all
export const organizationInventoryIndexRoute = createRoute({
	getParentRoute: () => organizationInventoryRoute,
	path: '/',
	beforeLoad: ({ params, search }) => {
		throw redirect({
			to: '/orgs/$orgId/inventory/$status',
			params: { ...params, status: DeviceStatus.All },
			search,
			replace: true,
		})
	},
})

// Inventory with status parameter
export const organizationInventoryStatusRoute = createRoute({
	getParentRoute: () => organizationInventoryRoute,
	path: '/$status',
	parseParams: params => statusParamsSchema.parse(params),
	validateSearch: deviceSearchSchema,
	beforeLoad: ({ search, cause, params }) => {
		// The sort lives in the URL, so a reload or shared link keeps it, and a switch to another organization (no ?sort)
		// lands on the default. A switch arrives as cause 'stay', so the scope is compared too. A search-only change on
		// the open page has already applied its own sort.
		if (cause === 'enter' || (cause === 'stay' && useOrgDeviceStore.getState().sortScope !== params.orgId)) {
			const { sortBy, sortOrder } = deviceSortFromSearch(search)
			useOrgDeviceStore.getState().restoreSort(sortBy, sortOrder, params.orgId)
		}
	},
	component: () => <DeviceManagementPage />,
})

export const organizationMetersRoute = createRoute({
	getParentRoute: () => organizationRoute,
	path: '/meters',
	component: () => <OrganizationMetersLayout />,
	beforeLoad: ({ context, params }) => {
		// meters route should be available only for platform deployments
		const orgId = resolveOrgId(params.orgId)
		platformOnlyRouteGuard(context)
		organizationLevelRouteGuard(context, orgId)
		useAuthStore.getState().clearCurrentTenantUnlessBelongsToOrg(orgId)
	},
})

export const organizationMetersIndexRoute = createRoute({
	getParentRoute: () => organizationMetersRoute,
	path: '/',
	beforeLoad: ({ params, search }) => {
		throw redirect({
			to: '/orgs/$orgId/meters/$module',
			params: { ...params, module: 'devices' },
			search,
			replace: true,
		})
	},
})

export const metersModuleSchema = z.object({
	module: z.enum(METERS_MODULE_IDS),
})

export const organizationMetersModuleRoute = createRoute({
	getParentRoute: () => organizationMetersRoute,
	path: '/$module',
	parseParams: params => metersModuleSchema.parse(params),
	component: () => {
		const { module } = organizationMetersModuleRoute.useParams()
		return <OrganizationMetersList module={module as MetersModule} />
	},
})

export const organizationOperationsRoute = createRoute({
	getParentRoute: () => organizationRoute,
	path: '/operations',
	component: () => <Outlet />,
	beforeLoad: async ({ context, params }) => {
		const orgId = resolveOrgId(params.orgId)
		platformOnlyRouteGuard(context)
		organizationLevelRouteGuard(context, orgId)
		useAuthStore.getState().clearCurrentTenantUnlessBelongsToOrg(orgId)

		// Detach/attach need allProjects loaded to resolve the root project.
		if (!useProjectsStore.getState().rootTenantId) {
			try {
				await useProjectsStore.getState().fetchProjects(undefined, undefined, undefined)
			} catch {
				// fetchProjects sets its own error state; proceed with whatever is available
			}
		}
	},
})

export const organizationOperationsIndexRoute = createRoute({
	getParentRoute: () => organizationOperationsRoute,
	path: '/',
	beforeLoad: redirectToAllTab('/orgs/$orgId/operations/$type'),
})

export const organizationOperationsTypeRoute = createRoute({
	getParentRoute: () => organizationOperationsRoute,
	path: '/$type',
	component: () => <RemoteCommandsPage />,
})

export const orgSettingsRoute = createRoute({
	getParentRoute: () => organizationRoute,
	path: '/settings',
	component: () => <SettingsPage />,
	beforeLoad: ({ context, params }) => {
		platformOnlyRouteGuard(context)
		useAuthStore.getState().clearCurrentTenantUnlessBelongsToOrg(resolveOrgId(params.orgId))
	},
})

export const orgSettingsIndexRoute = createRoute({
	getParentRoute: () => orgSettingsRoute,
	path: '/',
	beforeLoad: ({ params, search }) => {
		throw redirect({
			to: '/orgs/$orgId/settings/general',
			params,
			search,
			replace: true,
		})
	},
})

export const orgGeneralSettingsRoute = createRoute({
	getParentRoute: () => orgSettingsRoute,
	path: '/general',
	component: () => <OrganizationGeneralPage />,
})

export const orgUsersSettingsRoute = createRoute({
	getParentRoute: () => orgSettingsRoute,
	path: '/users',
	component: () => <Outlet />,
})
export const orgSettingsUsersIndexRoute = createRoute({
	getParentRoute: () => orgUsersSettingsRoute,
	path: '/',
	beforeLoad: redirectToAllTab('/orgs/$orgId/settings/users/$type'),
})
export const orgSettingsUsersTypeRoute = createRoute({
	getParentRoute: () => orgUsersSettingsRoute,
	path: '/$type',
	parseParams: params => tabAnySchema.parse(params),
	component: () => <UsersSettingsPage />,
})

export const orgApiKeysSettingsRoute = createRoute({
	getParentRoute: () => orgSettingsRoute,
	path: '/api-keys',
	component: () => <Outlet />,
	beforeLoad: async () => {
		const { rootTenantId } = useProjectsStore.getState()
		if (!rootTenantId) {
			try {
				await useProjectsStore.getState().fetchProjects(undefined, undefined, undefined)
			} catch {
				// fetchProjects sets its own error state; proceed with whatever rootTenantId is available
			}
		}
	},
})

export const orgSettingsApiKeysIndexRoute = createRoute({
	getParentRoute: () => orgApiKeysSettingsRoute,
	path: '/',
	beforeLoad: redirectToAllTab('/orgs/$orgId/settings/api-keys/$type'),
})

export const orgSettingsApiKeysTypeRoute = createRoute({
	getParentRoute: () => orgApiKeysSettingsRoute,
	path: '/$type',
	parseParams: params => apiTypeSchema.parse(params),
	component: () => <ApiKeysSettingsPage />,
})

// Org settings pages send rootTenantId as x-bytebeam-tenant, since their URL has no tenant segment.
const loadRootTenant = async () => {
	if (useProjectsStore.getState().rootTenantId) {
		return
	}
	try {
		await useProjectsStore.getState().fetchProjects(undefined, undefined, undefined)
	} catch {
		// fetchProjects sets its own error state; proceed with whatever rootTenantId is available
	}
}

// ORGANIZATION SETTINGS — Metadata (moved from Inventory)
export const orgSettingsMetadataRoute = createRoute({
	getParentRoute: () => orgSettingsRoute,
	path: '/metadata',
	// No route-level boundary: MetadataPage stays mounted so its header is kept; the body
	// self-restricts via per-resource RBAC. See MetadataPage.
	component: () => <Outlet />,
	beforeLoad: loadRootTenant,
})
export const orgSettingsMetadataIndexRoute = createRoute({
	getParentRoute: () => orgSettingsMetadataRoute,
	path: '/',
	beforeLoad: redirectToAllTab('/orgs/$orgId/settings/metadata/$type'),
})
export const orgSettingsMetadataTypeRoute = createRoute({
	getParentRoute: () => orgSettingsMetadataRoute,
	path: '/$type',
	parseParams: params => tabAllOnlySchema.parse(params),
	component: () => <MetadataPage />,
})

// ORGANIZATION SETTINGS — Streams
export const orgSettingsStreamsRoute = createRoute({
	getParentRoute: () => orgSettingsRoute,
	path: '/streams',
	component: () => <Outlet />,
	beforeLoad: loadRootTenant,
})
export const orgSettingsStreamsIndexRoute = createRoute({
	getParentRoute: () => orgSettingsStreamsRoute,
	path: '/',
	beforeLoad: redirectToAllTab('/orgs/$orgId/settings/streams/$type'),
})
export const orgSettingsStreamsTypeRoute = createRoute({
	getParentRoute: () => orgSettingsStreamsRoute,
	path: '/$type',
	parseParams: params => tabAnySchema.parse(params),
	component: () => <StreamsPage />,
})

// No boundary at route level: DeviceStatePage keeps its header mounted and restricts its own body.
export const orgSettingsDeviceStateRoute = createRoute({
	getParentRoute: () => orgSettingsRoute,
	path: '/device-state',
	component: () => <Outlet />,
	beforeLoad: loadRootTenant,
})
export const orgSettingsDeviceStateIndexRoute = createRoute({
	getParentRoute: () => orgSettingsDeviceStateRoute,
	path: '/',
	beforeLoad: redirectToAllTab('/orgs/$orgId/settings/device-state/$type'),
})
export const orgSettingsDeviceStateTypeRoute = createRoute({
	getParentRoute: () => orgSettingsDeviceStateRoute,
	path: '/$type',
	parseParams: params => tabAllOnlySchema.parse(params),
	component: () => <DeviceStatePage />,
})

/** Everything under an organization URL. Project routes nest here, since their URL does too. */
export const organizationRouteTree = organizationRoute.addChildren([
	organizationBillingRoute.addChildren([organizationBillingIndexRoute, organizationBillingTypeRoute]),
	organizationAccessDeniedRoute,
	organizationInventoryRoute.addChildren([organizationInventoryIndexRoute, organizationInventoryStatusRoute]),
	organizationMetersRoute.addChildren([organizationMetersIndexRoute, organizationMetersModuleRoute]),
	organizationOperationsRoute.addChildren([organizationOperationsIndexRoute, organizationOperationsTypeRoute]),
	orgSettingsRoute.addChildren([
		orgSettingsIndexRoute,
		orgGeneralSettingsRoute,
		orgUsersSettingsRoute.addChildren([orgSettingsUsersIndexRoute, orgSettingsUsersTypeRoute]),
		orgApiKeysSettingsRoute.addChildren([orgSettingsApiKeysIndexRoute, orgSettingsApiKeysTypeRoute]),
		orgSettingsMetadataRoute.addChildren([orgSettingsMetadataIndexRoute, orgSettingsMetadataTypeRoute]),
		orgSettingsStreamsRoute.addChildren([orgSettingsStreamsIndexRoute, orgSettingsStreamsTypeRoute]),
		orgSettingsDeviceStateRoute.addChildren([orgSettingsDeviceStateIndexRoute, orgSettingsDeviceStateTypeRoute]),
		organizationAuditLogsRoute.addChildren([organizationAuditLogsIndexRoute, organizationAuditLogsTypeRoute]),
	]),
	organizationProjectRouteTree,
])
